import type { ReactNode } from 'react';

import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import { Editor, Files, Time, Users, Weather } from '../StrokeIcons';

export type ContactDetailTab = 'action' | 'interact' | 'data' | 'dossier';

export type ContactDetailTabsProps = {
  activeTab: ContactDetailTab;
  onTabChange?: (tab: ContactDetailTab) => void;
  hasNotes: boolean;
  notesOpen: boolean;
  onToggleNotes: () => void;
  hideActionTab?: boolean;
  hideInteractTab?: boolean;
  hideDataTab?: boolean;
  hideDossierTab?: boolean;
  hideNotesButton?: boolean;
  /** Optional action button(s) floated to the right of the tab row. Used by ContactDetailPageCore
      to place the small Refresh & Enrich icon next to the tabs without disturbing tab centering. */
  rightAction?: ReactNode;
};

export function ContactDetailTabs({
  activeTab,
  onTabChange,
  hasNotes,
  notesOpen,
  onToggleNotes,
  hideActionTab,
  hideInteractTab,
  hideDataTab,
  hideDossierTab,
  hideNotesButton,
  rightAction,
}: ContactDetailTabsProps) {
  return (
    <div className="relative flex items-center justify-center gap-2">
      <ButtonGroup size="sm" shape="pill" color="blue" tone="outline">
        {!hideActionTab && (
          <Button
            tooltip="Quick Actions"
            active={activeTab === 'action'}
            icon={<Weather.Lightning02 />}
            onClick={() => onTabChange?.('action')}
          />
        )}
        {!hideInteractTab && (
          <Button
            tooltip="Campaign & History"
            active={activeTab === 'interact'}
            icon={<Time.ClockRewind />}
            onClick={() => onTabChange?.('interact')}
          />
        )}
        {!hideDataTab && (
          <Button
            tooltip="Contact Info"
            active={activeTab === 'data'}
            icon={<Users.User03 />}
            onClick={() => onTabChange?.('data')}
          />
        )}
        {!hideDossierTab && (
          <Button
            tooltip="Dossier"
            active={activeTab === 'dossier'}
            icon={<Files.File02 />}
            onClick={() => onTabChange?.('dossier')}
          />
        )}
      </ButtonGroup>
      {!hideNotesButton && (
        <Button
          size="sm"
          color={hasNotes ? 'yellow' : undefined}
          tone={hasNotes ? 'outline' : undefined}
          shape="pill"
          tooltip="Notes"
          active={notesOpen}
          icon={<Editor.Pencil01 />}
          onClick={onToggleNotes}
        />
      )}
      {rightAction != null && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2">{rightAction}</div>
      )}
    </div>
  );
}

export default ContactDetailTabs;
